import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, ClipboardList, Layout as LayoutIcon, Calendar, Menu, X } from 'lucide-react';
import { useState } from 'react';
import { ROUTE_PATHS } from '@/lib/index';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

interface LayoutProps {
  children: React.ReactNode;
}

const navigationItems = [
  {
    path: ROUTE_PATHS.HOME,
    label: '프로젝트 개요',
    icon: FileText,
  },
  {
    path: ROUTE_PATHS.REQUIREMENTS,
    label: '시스템 요구사항',
    icon: ClipboardList,
  },
  {
    path: ROUTE_PATHS.SYSTEM_DESIGN,
    label: '시스템 설계',
    icon: LayoutIcon,
  },
  {
    path: ROUTE_PATHS.SCHEDULE,
    label: '개발 일정',
    icon: Calendar,
  },
];

export function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const NavContent = () => (
    <nav className="space-y-2">
      {navigationItems.map((item) => {
        const Icon = item.icon;
        const isActive = location.pathname === item.path;
        
        return (
          <Link
            key={item.path}
            to={item.path}
            onClick={() => setMobileMenuOpen(false)}
            className="block"
          >
            <motion.div
              whileHover={{ scale: 1.02, x: 4 }}
              whileTap={{ scale: 0.98 }}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                isActive
                  ? 'bg-primary text-primary-foreground shadow-lg'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              }`}
            >
              <Icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </motion.div>
          </Link>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 flex h-16 items-center justify-between">
          <div className="flex items-center gap-4">
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72">
                <div className="py-6">
                  <h2 className="text-lg font-semibold mb-6">메뉴</h2>
                  <NavContent />
                </div>
              </SheetContent>
            </Sheet>
            
            <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-2">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FileText className="w-6 h-6 text-primary" />
              </motion.div>
              <div>
                <h1 className="text-lg font-bold">프로젝트 관리 시스템</h1>
                <p className="text-xs text-muted-foreground">폴리텍 대학 스마트팩토리과</p>
              </div>
            </Link>
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground hidden sm:inline">2026학년도</span>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        <div className="flex gap-6">
          <aside className="hidden lg:block w-64 shrink-0">
            <div className="sticky top-24 space-y-6">
              <div className="bg-card rounded-lg border border-border p-4 shadow-sm">
                <h2 className="text-sm font-semibold mb-4 text-muted-foreground">개발계획서</h2>
                <NavContent />
              </div>
              
              <div className="bg-card rounded-lg border border-border p-4 shadow-sm">
                <h3 className="text-sm font-semibold mb-2">프로젝트 정보</h3>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex justify-between">
                    <span>학년도</span>
                    <span className="font-medium text-foreground">2026</span>
                  </div>
                  <div className="flex justify-between">
                    <span>학기</span>
                    <span className="font-medium text-foreground">1학기/2학기</span>
                  </div>
                  <div className="flex justify-between">
                    <span>총 주차</span>
                    <span className="font-medium text-foreground">16주</span>
                  </div>
                </div>
              </div>
            </div>
          </aside>

          <main className="flex-1 min-w-0">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {children}
            </motion.div>
          </main>
        </div>
      </div>

      <footer className="border-t border-border mt-12">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-muted-foreground">
            <p>© 2026 폴리텍 대학 스마트팩토리과. All rights reserved.</p>
            <p>프로젝트 관리 시스템 개발계획서</p>
          </div>
        </div>
      </footer>
    </div>
  );
}